import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { regionImage, ownerDisplayName, ownerPhotoSrc } from "@/lib/regions";
import { ConnectOwnerForm } from "@/components/region/ConnectOwnerForm";
import { ArrowLeft, Calendar, Linkedin, Mail, MapPin, MessageCircle, Phone, UserRound } from "lucide-react";

const PILL = "inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border border-gray-200 text-navy hover:text-white transition-colors";

function ContactButtons({ c }) {
  const items = [
    c.owner_phone && [`tel:${c.owner_phone}`, Phone, "Call", "hover:bg-navy", "owner-call-btn"],
    c.owner_whatsapp && [`https://wa.me/${c.owner_whatsapp.replace(/\D/g, "")}`, MessageCircle, "WhatsApp", "hover:bg-[#25D366]", "owner-whatsapp-btn"],
    c.owner_email && [`mailto:${c.owner_email}`, Mail, "Email", "hover:bg-navy", "owner-email-btn"],
    c.owner_linkedin && [c.owner_linkedin, Linkedin, "LinkedIn", "hover:bg-[#0a66c2]", "owner-linkedin-btn"],
  ].filter(Boolean);
  if (!items.length) return <p data-testid="owner-contact-pending" className="text-sm text-gray-500">Direct contact details coming soon. Use the form to reach the owner.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(([href, Icon, label, hover, id]) => (
        <a key={id} href={href} data-testid={id} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={`${PILL} ${hover}`}>
          <Icon size={15} /> {label}
        </a>
      ))}
    </div>
  );
}

function OwnerProfile({ c }) {
  return (
    <div data-testid="owner-profile" className="grid md:grid-cols-12 gap-8 items-start">
      <div className="md:col-span-4">
        <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-cream border border-gray-200 flex items-center justify-center">
          {c.owner_photo_url
            ? <img src={ownerPhotoSrc(c.owner_photo_url)} alt={ownerDisplayName(c)} className="w-full h-full object-cover object-top" data-testid="owner-photo" />
            : <UserRound size={80} className="text-navy/25" data-testid="owner-photo-placeholder" />}
        </div>
      </div>
      <div className="md:col-span-8">
        <div className="overline text-gold mb-3">Meet the owner</div>
        <h2 data-testid="owner-name" className="text-3xl sm:text-4xl font-display font-semibold text-navy">{ownerDisplayName(c)}</h2>
        <div className="mt-1 text-gray-500">{c.owner_title || `Region Owner, D32 India ${c.city}`}</div>
        <p data-testid="owner-bio" className="mt-6 text-gray-600 leading-relaxed max-w-2xl">
          {c.owner_bio || `The ${c.city} region owner leads every D32 meeting in the region, welcomes visitors and helps business owners find the right membership. Full profile coming soon.`}
        </p>
        {c.owner_quote && (
          <blockquote data-testid="owner-quote" className="mt-8 max-w-2xl border-l-4 border-gold pl-6">
            <p className="font-display font-medium text-lg sm:text-xl text-navy leading-relaxed italic">"{c.owner_quote}"</p>
            <footer className="mt-3 text-sm text-gray-500">{c.owner_name}</footer>
          </blockquote>
        )}
        <div className="mt-8"><ContactButtons c={c} /></div>
      </div>
    </div>
  );
}

function RegionHero({ c }) {
  return (
    <section className="relative bg-navy text-white overflow-hidden">
      <img src={regionImage(c.slug)} alt={c.city} className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="relative container-narrow py-20 lg:py-28">
        <Link to="/find-a-region" data-testid="region-back-link" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"><ArrowLeft size={14} /> All regions</Link>
        <div className="mt-6 overline text-gold">D32 India · Live region</div>
        <h1 data-testid="region-title" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-semibold">{c.city}</h1>
        <p className="mt-3 text-gray-300">{c.state}</p>
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-gray-200">
          <span data-testid="region-schedule" className="flex items-center gap-2"><Calendar size={15} className="text-gold" />{c.meeting_schedule || "Schedule coming soon"}</span>
          <span data-testid="region-venue" className="flex items-center gap-2"><MapPin size={15} className="text-gold" />{c.meeting_venue || "Venue to be announced"}</span>
        </div>
      </div>
    </section>
  );
}

export default function RegionOwner() {
  const { slug } = useParams();
  const [circle, setCircle] = useState(undefined);

  useEffect(() => {
    api.get(`/circles/${slug}`).then((r) => setCircle(r.data)).catch(() => setCircle(null));
  }, [slug]);

  if (circle === undefined) return <div className="py-40 text-center text-gray-500">Loading region…</div>;
  if (!circle || !circle.is_live) {
    return (
      <div data-testid="region-not-found" className="py-40 text-center container-narrow">
        <h1 className="font-display text-3xl text-navy">This region isn't live yet</h1>
        <Link to="/find-a-region" className="mt-6 inline-flex bg-navy text-white px-6 py-3 rounded-full font-semibold">See live regions</Link>
      </div>
    );
  }

  return (
    <div data-testid="region-owner-page">
      <RegionHero c={circle} />
      <section className="py-20 lg:py-24 bg-white"><div className="container-narrow"><OwnerProfile c={circle} /></div></section>
      <section className="py-20 lg:py-24 bg-cream">
        <div className="container-narrow grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline text-gold mb-3">Get in touch</div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">Visit a meeting or ask about membership.</h2>
            {circle.description && <p className="mt-5 text-gray-600 leading-relaxed max-w-md">{circle.description}</p>}
            <Link to="/visit-a-circle" data-testid="region-visit-link" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-navy text-navy font-semibold hover:bg-navy hover:text-white transition-colors">Book a visit instead</Link>
          </div>
          <div className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-200"><ConnectOwnerForm circle={circle} /></div>
        </div>
      </section>
    </div>
  );
}
